
import { ColDef, GetRowIdParams, ValueFormatterParams } from "ag-grid-community";
import PBS from "assets/images/PBS.png";
import { FormInput, Modal, Spinner, TooltipIcon } from "components";
import { AgGrid, AgGridRef } from "components/AgGrid/AgGrid";
import { ZButton } from "components/ZButton";
import { ZFeatureFlag } from "components/ZFeatureFlag";
import { ZIcon } from "components/ZIcon";
import { ZLabel } from "components/ZLabel";
import { ZMultiSelectDropdown } from "components/ZMultiSelectDropdown";
import { ZNumberInput } from "components/ZNumberInput";
import { ZSingleSelectDropdown } from "components/ZSingleSelectDropdown";
import { ZTextInput } from "components/ZTextInput";
import { ZTooltip } from "components/ZTooltip";
import { getMainColors } from "constants/colors";
import { statesToString } from "constants/constants";
import { ZIconType } from "helpers/utility/iconMap";
import useToggle from "hooks/useToggle";
import { ManagedStockView } from "models/dbModels/managedStock/ManagedStock";
import { ManagedStockApn } from "models/dbModels/managedStock/ManagedStockApn";
import { ManagedStockPde } from "models/dbModels/managedStock/ManagedStockPde";
import { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getDepartments } from "redux/slices/Departments";
import { getManufacturers } from "redux/slices/ManagedManufacturers";
import { createStockGroup, getStockGroups } from "redux/slices/StockGroups";
import { createStock, getStock, updateModelField, updateStock, updateStockField } from "redux/slices/Stocks";
import { getStoreStocks } from "redux/slices/StoreStocks";
import { getSubDepartments } from "redux/slices/SubDepartment";
import { showToast } from "redux/slices/Toasts";
import { RootState } from "redux/store";
import OverflowLabel from "components/OverflowLabel";
import { v4 as uuidv4 } from 'uuid';
import { ManagedStockSync } from "../Managed/Popups/ManagedStockSync";
import PDE from "../Managed/NewStocks/General/PDE";
import APN from "../Managed/NewStocks/General/APN";

type ManagedStockFormProps = ManagedStockView;

export const OldGeneral = () => {
    enum GSTCode {
        F = "F",
        N = "N",
        Y = "Y"
    };

    const gstCodes: Map<GSTCode, string> = new Map<GSTCode, string>([
        [GSTCode.F, "0% Buy, 0% Sell"],
        [GSTCode.N, "10% Buy, 0% Sell"],
        [GSTCode.Y, "10% Buy, 10% Sell"]
    ]);

    const defaultValues: ManagedStockFormProps = {
        name: "",
        dispenseName: null,
        schedule: null,
        isPbs: false,
        stockGroupIds: [],
        pdes: [],
        apns: [],
        id: 0,
        storeGroupId: 0,
        stockId: "",
        productNo: null,
        departmentId: null,
        subdepartmentId: null,
        manufacturerId: null,
        supplierId: null,
        cartonSize: 1,
        minOrderQty: 1,
        minStockLevel: 1,
        maxStockLevel: null,
        gstCode: "N",
        active: true,
        ethical: false,
        locked: false,
        costEx: 0,
        baseSellPrice: 0,
        rrp: null,
        additionalOrderInformation: null
    }
    const {
        handleSubmit,
        control,
        reset,
        getValues,
        setValue,
        setError,
        trigger,
        formState: { errors },
    } = useForm<ManagedStockFormProps>({
        defaultValues: defaultValues,
        mode: "onChange",
        reValidateMode: "onChange",
    });

    type PdeRowData = ManagedStockPde & {
        supplierName: string | undefined;
        rowId: string;
    }

    type ApnRowData = ManagedStockApn & {
        rowId: string;
    }

    const form = useRef(null);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showMenu, setShowMenu] = useToggle(false);
    const [pdes, setPdes] = useState<PdeRowData[]>([]);
    const [apns, setApns] = useState<ApnRowData[]>([]);
    const [isValidated, setValidated] = useState<boolean>(false);
    const stocks = useAppSelector((state: RootState) => state.stocks);
    const suppliers = useAppSelector((state: RootState) => state.suppliers);
    const departments = useAppSelector((state: RootState) => state.department);
    const subDepartments = useAppSelector((state: RootState) => state.subDepartment);
    const stockGroups = useAppSelector((state: RootState) => state.stockGroups);
    const manufacturers = useAppSelector((state: RootState) => state.managedManufacturers);
    const [isNewModel, setIsNewModel] = useState<boolean>(true);
    const [syncOpen, setSyncOpen] = useState<boolean>(false);
    const [openSyncWindow, setOpenSyncWindow] = useState<boolean>(false);
    const [selectedPde, setSelectedPde] = useState<PdeRowData | undefined>();
    const [selectedApn, setSelectedApn] = useState<ApnRowData | undefined>();
    const [showDeletePdeConfirmPrompt, setShowDeletePdeConfirmPrompt] = useState<boolean>(false);
    const [showDeleteApnConfirmPrompt, setShowDeleteApnConfirmPrompt] = useState<boolean>(false);
    const pdeGridRef = useRef<AgGridRef>(null);
    const apnGridRef = useRef<AgGridRef>(null);

    const pdeColumnDefs: ColDef<PdeRowData>[] = [
        {
            headerName: "PDE",
            sortable: true,
            field: "pde",
            minWidth: 90,
            checkboxSelection: true,
            cellRendererSelector: (params) => {
                return {
                    component: 'baseCellRenderer',
                    params: {
                        decorator: <ZTooltip enabled={params.data?.isSystem} placement="top" text="This is a system PDE"><ZIcon icon={ZIconType.System} size="14px" iconClassName={`me-1 ${params.data?.isSystem ? "" : "invisible"}`} /></ZTooltip>,
                        decoratorPosition: "start",
                    }
                }
            },
        },
        {
            headerName: "Supplier",
            field: "supplierName",
            sortable: true,
            minWidth: 110,
        },
        {
            headerName: "States",
            field: "states",
            sortable: true,
            minWidth: 90,
            flex: 1,
            valueFormatter: (params: ValueFormatterParams) => {
                return statesToString(params.value);
            },
        }
    ];

    const apnColumnDefs: ColDef<ApnRowData>[] = [
        {
            headerName: "APN",
            field: "apn",
            sortable: true,
            minWidth: 90,
            flex: 1,
            checkboxSelection: true,
            cellRendererSelector: (params) => {
                return {
                    component: 'baseCellRenderer',
                    params: {
                        decorator: <ZTooltip enabled={params.data?.isSystem} placement="top" text="This is a system APN"><ZIcon icon={ZIconType.System} size="14px" iconClassName={`me-1 ${params.data?.isSystem ? "" : "invisible"}`} /></ZTooltip>,
                        decoratorPosition: "start",
                    }
                }
            }
        }
    ];

    useEffect(() => {
        setValidated(false);
        dispatch(getDepartments());
        dispatch(getStockGroups());
        dispatch(getManufacturers())

        if (stocks.model.stockId) {
            dispatch(getStoreStocks(stocks.model.stockId)).then((_) => {
                setIsNewModel(false);
                reset({
                    ...stocks.model
                } as ManagedStockFormProps);
            });
        } else {
            reset(defaultValues);
            setIsNewModel(true);
        }

        // Check if we have openSync in the query param, if we do, open the sync popup and remove the param
        if (searchParams.has('openSync')) {
            if (searchParams.get('openSync') === "true") {
                setSyncOpen(true);
            }
            searchParams.delete('openSync');
            setSearchParams(searchParams);
        }
    }, [stocks.model.stockId]);

    useEffect(() => {
        setStockGroupsDropdown();
    }, [stockGroups.table]);

    useEffect(() => {
        stocks.model.departmentId && dispatch(getSubDepartments(stocks.model.departmentId));
    }, [stocks.model.departmentId]);

    // TODO: Need to refactor this. Redux has complicated things since we need the system code for the supplier in this grid but managed stock pde only has the id
    // TODO: and the redux state updates too late so the grid doesn't have the suppliers yet when it renders. This useEffect "tacks on" supplier code to the pde grid model
    // TODO: so it can at least show it but I'm not happy with the solution.
    // useEffect(() => {
    //     if (suppliers.table.length > 0) {
    //         setPdes(pdes => pdes.map(x => {
    //             return {...x, supplierName: suppliers.table.find(s => s.id === x.supplierId)?.name};
    //         }));
    //     }
    // }, [suppliers.table]);

    useEffect(() => {
        if (openSyncWindow) {
            handleChange("loadingSync", true);
            handleFormSubmit();
            setOpenSyncWindow(false);
        }
    }, [openSyncWindow]);

    useEffect(() => {
        handleMarkupGP();
    }, [stocks.model.costEx, stocks.model.baseSellPrice, stocks.model.gstCode]);

    useEffect(() => {
        setPdes(stocks.model.pdes.map(pde => {
            return {
                ...pde,
                rowId: uuidv4(),
                supplierName: suppliers.table.find(s => s.id === pde.supplierId)?.name ?? "No supplier"
            }
        }));
    }, [stocks.model.pdes]);

    useEffect(() => {
        setPdes(stocks.model.pdes.map(pde => {
            return {
                ...pde,
                rowId: uuidv4(),
                supplierName: suppliers.table.find(s => s.id === pde.supplierId)?.name ?? "No supplier"
            }
        }));
    }, [suppliers.table]);

    useEffect(() => {
        setApns(stocks.model.apns.map(apn => {
            return {
                ...apn,
                rowId: uuidv4()
            }
        }));
    }, [stocks.model.apns])

    function handleNewPde(pde: ManagedStockPde) {
        invalidateStock();
        if (selectedPde) {
            const oldPdeIndex = pdes.findIndex(p => p.rowId === selectedPde.rowId);
            let newPdes = [...pdes];
            newPdes[oldPdeIndex] = { ...pde, rowId: selectedPde.rowId, supplierName: suppliers.table.find(s => s.id === pde.supplierId)?.name };

            setPdes(newPdes);
        } else {
            setPdes(pdes => [...pdes.map(x => {
                return { ...x, supplierName: suppliers.table.find(s => s.id === x.supplierId)?.name };
            }), { ...pde, isSystem: false, rowId: uuidv4(), supplierName: suppliers.table.find(s => s.id === pde.supplierId)?.name }]);
        }

        closePdePopup();
    }

    function handleNewApn(apn: ManagedStockApn) {
        invalidateStock();
        if (selectedApn) {
            const oldApnIndex = apns.findIndex(a => a.id === apn.id);
            let newApns = [...apns];
            newApns[oldApnIndex] = { ...apn, rowId: selectedApn.rowId };
            setApns(newApns);
        } else {
            setApns([...apns, { ...apn, rowId: uuidv4() }]);
        }

        closeApnPopup();
    }

    function handleChange(option: any, optionValue: any) {
        dispatch(updateStockField({ key: option, value: optionValue }));
    }

    function handleModelChange(option: any, optionValue: any) {
        handleChange("saveSuccess", false);
        dispatch(updateModelField({ key: option, value: optionValue }));
    }

    function invalidateStock() {
        handleChange("saveSuccess", false);
    }

    async function handleFormSubmit() {
        handleChange("saveSuccess", false);
        handleSubmit(async (validData) => {
            const formValues = cleanModel(getValues());
            formValues.name = formValues.name.trim();

            if (isNewModel) {
                dispatch(createStock([{ ...formValues, pdes: pdes, apns: apns }])).then((res) => {
                    if (res.data) {
                        dispatch(updateStockField({ key: "saveSuccess", value: true }));
                        !isNewModel && dispatch(getStock(stocks.model.id));

                        if (openSyncWindow) {
                            handleChange("loadingSync", false);

                            if (isNewModel) {
                                navigate(`/app/stock/${res.data}?openSync=true`);
                            }
                            else {
                                setSyncOpen(true);
                            }
                        } else {
                            handleChange("loading", false);
                            isNewModel && navigate(`/app/stock/${res.data}`);
                        }
                    } else {
                        handleChange("loading", false);
                        handleChange("loadingSync", false);
                    }
                }).catch((err) => {
                    handleChange("loading", false);
                    handleChange("loadingSync", false);
                });
            } else {
                dispatch(updateStock([{ ...formValues, pdes: pdes, apns: apns }])).then((res) => {
                    if (res.data) {
                        dispatch(updateStockField({ key: "saveSuccess", value: true }));
                        if (openSyncWindow) {
                            handleChange("loadingSync", false);

                            if (isNewModel) {
                                navigate(`/app/stock/${res.data}?openSync=true`);
                            }
                            else {
                                setSyncOpen(true);
                            }
                        } else {
                            handleChange("loading", false);
                            isNewModel && navigate(`/app/stock/${res.data}`);
                        }
                    } else {
                        handleChange("loading", false);
                        handleChange("loadingSync", false);
                    }
                    dispatch(getStock(stocks.model.id)); 
                }).catch(() => {
                    handleChange("loading", false);
                    handleChange("loadingSync", false);
                });
            }
        },
            (invalidData) => {
                handleChange("loading", false);
                handleChange("loadingSync", false);
            })();
    }

    function handleMarkupGP() {
        let salesEx = Number(stocks.model.baseSellPrice) / (stocks.model.gstCode === "Y" ? 1.1 : 1);
        let markup = (salesEx - stocks.model.costEx) / stocks.model.costEx * 100;
        if (!isNaN(markup) && isFinite(markup)) {
            handleChange("markup", markup.toFixed(2));
        } else {
            handleChange("markup", undefined);
        }
        let gp = (salesEx - stocks.model.costEx) / salesEx * 100;
        if (!isNaN(gp)) {
            handleChange("gp", gp.toFixed(2));
        } else {
            handleChange("gp", undefined);
        }
    }

    function closePdePopup() {
        handleChange("showPdePopup", false);
    }

    function closeApnPopup() {
        handleChange("showApnPopup", false);
    }

    function handleBlur(option: any, optionValue: any) {
        if (!isNaN(optionValue)) {
            if (optionValue) {
                let value = parseFloat(optionValue).formatAsPrice();
                handleModelChange(option, value);
            }
        } else {
            handleModelChange(option, "");
        }
    }

    function setStockGroupsDropdown() {
        let stockGroupsDropdown: any[] = [];
        stockGroups.table.forEach((item: any) => {
            //@ts-ignore
            stockGroupsDropdown.push({ id: item.id, label: item.name, checked: stocks.model.stockGroupIds.includes(item.id) ? true : false });
        });
        handleChange("stockGroupsDropdown", stockGroupsDropdown);
    }

    const RequiredComponent = (text: string | React.ReactNode, tooltipText: string) => {
        return (
            <ZLabel>
                <span className="d-inline-flex align-items-center gap-1 w-100">
                    {text}
                    <ZTooltip enabled placement='top' text={tooltipText}>
                        <>*</>
                    </ZTooltip>
                </span>
            </ZLabel>
        );
    }
    

    function cleanModel(model: ManagedStockView): ManagedStockView  {
        //we'll quickly run through the model to remove any invalid Ids (eg subdepartments that don't exist on this store group).
        //This shouldn't occur though, but just incase

        let temp = {...model};
        
        if (model.departmentId != null && !departments.table.some(i => i.id === model.departmentId)){
            temp.departmentId = null;
        }

        if (model.subdepartmentId != null && !subDepartments.table.some(i => i.id === model.subdepartmentId && i.departmentId === model.departmentId)) {
            temp.subdepartmentId = null;
        }

        if (model.manufacturerId != null && !manufacturers.table.some(i => i.id === model.manufacturerId)){
            temp.manufacturerId = null;
        }

        if (model.supplierId != null && !suppliers.table.some(i => i.id === model.supplierId)){
            temp.supplierId = null;
        }

        if (model.stockGroupIds != null) {
            temp.stockGroupIds = model.stockGroupIds.filter(i => stockGroups.table.some(j => j.id === i));
        }

        return temp;
    }

    
    return (
        <>
            <Modal
                title="Confirm - Delete PDE"
                show={showDeletePdeConfirmPrompt}
                buttonText="Delete"
                buttonType="danger"
                buttonCloseText="Cancel"
                handleClose={() => setShowDeletePdeConfirmPrompt(false)}
                handleSubmit={() => {
                    setShowDeletePdeConfirmPrompt(false);

                    // Delete allowed
                    // Have to match on pde/supplierId pair since id is 0 for new pdes
                    const ixToDelete = pdes.findIndex(pde => pde.pde === selectedPde!.pde && pde.supplierId === selectedPde!.supplierId);
                    setPdes(pdes => {
                        const newPdes = pdes.slice();
                        newPdes.splice(ixToDelete, 1);
                        return newPdes;
                    });
                }}
                description={
                    <>
                        <p>This will remove the PDE at the store after the Stock Sync.</p>
                        <p>Are you sure you want to remove the selected PDE?</p>
                    </>
                }
            />

            <Modal
                title="Confirm - Delete APN"
                show={showDeleteApnConfirmPrompt}
                buttonText="Delete"
                buttonType="danger"
                buttonCloseText="Cancel"
                handleClose={() => setShowDeleteApnConfirmPrompt(false)}
                handleSubmit={() => {
                    setShowDeleteApnConfirmPrompt(false);

                    // Have to match on apn since id is 0 for new apns
                    const ixToDelete = apns.findIndex(apn => apn.apn === selectedApn!.apn);
                    setApns(apns => {
                        const newApns = apns.slice();
                        newApns.splice(ixToDelete, 1);
                        return newApns;
                    });
                }}
                description={
                    <>
                        <p>This will remove the APN at the store after the Stock Sync.</p>
                        <p>Are you sure you want to remove the selected APN?</p>
                    </>
                }
            />
            <Row>
                <ManagedStockSync
                    isOpen={syncOpen}
                    onClose={() => {
                        setSyncOpen(false);
                    }}
                    counter={1}
                    filter={{ ids: [stocks.model.id] }}
                />
                <Col xs={12}>
                    <Row className="mb-1">
                        <Form onSubmit={handleFormSubmit} validated={isValidated} ref={form}>
                            <Row>
                                <Col md={4} className="General mt-2">
                                    <Row>
                                        <h6 className="form-title py-1">Stock Details</h6>
                                        <Col md={12}>
                                            <Controller
                                                control={control}
                                                rules={{
                                                    required: { value: true, message: "Please enter a name." },
                                                    maxLength: { value: 100, message: "Name cannot exceed 100 characters." },
                                                }}
                                                render={({ field: { onChange, onBlur, value } }) =>
                                                    <ZTextInput
                                                        label={RequiredComponent("Name", "This field is required")}
                                                        placeholder="Enter a name"
                                                        value={value}
                                                        validation={
                                                            {
                                                                invalid: errors.name != null,
                                                                error: errors.name?.message
                                                            }
                                                        }
                                                        onChange={(x) => { onChange(x); invalidateStock(); }}
                                                        onBlur={onBlur}
                                                    />
                                                }
                                                name={'name'}
                                            />
                                            {/* <FormInput
                                                required
                                                label="Name"
                                                type="text"
                                                name="stock-name"
                                                containerClass={"mb-2"}
                                                value={stocks.model.name}
                                                key="text"
                                                onChange={(e) => handleModelChange("name", e.target.value)}
                                            /> */}
                                        </Col>

                                        <Col md={12} className="mt-2">
                                            <Controller
                                                control={control}
                                                render={({ field: { onChange, onBlur, value, ref }, fieldState, formState, }) => {
                                                    return <ZSingleSelectDropdown
                                                        id="supplier"
                                                        canClear
                                                        label={<ZLabel>Supplier</ZLabel>}
                                                        placeholder="Select a supplier"
                                                        options={suppliers.table}
                                                        value={suppliers.table.find(s => s.id === value)}
                                                        validation={
                                                            {
                                                                invalid: errors.supplierId != null,
                                                                error: errors.supplierId?.message
                                                            }
                                                        }
                                                        onBlur={onBlur}
                                                        optionKey={(opt) => opt.id}
                                                        disabled={() => false}
                                                        display={(opt) => opt.name ?? "Unknown"}
                                                        onSelectionChange={(newSupplier) => {
                                                            handleModelChange("supplierId", newSupplier?.id ?? null);
                                                            newSupplier ? onChange(newSupplier.id) : onChange(null);
                                                        }}
                                                        hasSearch
                                                    />
                                                }}
                                                name={'supplierId'}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={12} className="mt-2">
                                            <Controller
                                                control={control}
                                                rules={{}}
                                                render={({ field: { onChange, onBlur, value, ref }, fieldState, formState, }) => {
                                                    return <ZSingleSelectDropdown
                                                        id="dept"
                                                        canClear
                                                        label={<ZLabel>Department</ZLabel>}
                                                        placeholder="Select a department"
                                                        options={departments.table}
                                                        value={departments.table.find(d => d.id === value)}
                                                        validation={
                                                            {
                                                                invalid: errors.departmentId != null,
                                                                error: errors.departmentId?.message
                                                            }
                                                        }
                                                        onBlur={onBlur}
                                                        optionKey={(opt) => opt.id}
                                                        disabled={() => false}
                                                        display={(opt) => opt.name ?? "Unknown"}
                                                        onSelectionChange={(newDept) => {
                                                            handleModelChange("departmentId", newDept?.id ?? null);
                                                            setValue("subdepartmentId", null);
                                                            if (newDept) {
                                                                onChange(newDept.id);
                                                            } else {
                                                                onChange(null);
                                                            }
                                                        }}
                                                        hasSearch
                                                    />
                                                }}
                                                name={'departmentId'}
                                            />
                                            {/* <Form.Group>
                                                <Form.Label className="form-check-label">Department</Form.Label>
                                                <Form.Select
                                                    className="custom-select"
                                                    value={stocks.model.departmentId ?? undefined}
                                                    onChange={(e) => {
                                                        handleModelChange("departmentId", parseInt(e.target.value));
                                                        if (parseInt(e.target.value) === null) {
                                                            handleModelChange("subdepartmentId", parseInt(e.target.value))
                                                        }
                                                    }}
                                                >
                                                    <option value=""> </option>
                                                    {departments.table.map((department: any, index: any) => {
                                                        return <option key={index} value={department.id}>{department.name}</option>
                                                    })}
                                                </Form.Select>
                                            </Form.Group> */}
                                        </Col>
                                        <Col md={12} className="mt-2">
                                            <Controller
                                                control={control}
                                                rules={{}}
                                                render={({ field: { onChange, onBlur, value, ref }, fieldState, formState, }) => {
                                                    return <ZSingleSelectDropdown
                                                        id="subdept"
                                                        canClear
                                                        label={<ZLabel>Sub Department</ZLabel>}
                                                        placeholder="Select a sub department"
                                                        options={stocks.model.departmentId ? subDepartments.table : []}
                                                        value={subDepartments.table.find(s => s.id === value)}
                                                        validation={
                                                            {
                                                                invalid: errors.subdepartmentId != null,
                                                                error: errors.subdepartmentId?.message
                                                            }
                                                        }
                                                        onBlur={onBlur}
                                                        optionKey={(opt) => opt.id}
                                                        disabled={() => false}
                                                        display={(opt) => opt.name ?? "Unknown"}
                                                        onSelectionChange={(newSubDept) => {
                                                            handleModelChange("subdepartmentId", newSubDept?.id ?? null);
                                                            newSubDept ? onChange(newSubDept.id) : onChange(null);
                                                        }}
                                                        hasSearch
                                                    />
                                                }}
                                                name={'subdepartmentId'}
                                            />
                                            {/* <Form.Group>
                                                <Form.Label className="form-check-label">Sub Department</Form.Label>
                                                <Form.Select
                                                    className="custom-select"
                                                    value={stocks.model.subdepartmentId ?? undefined}
                                                    onChange={(e) => handleModelChange("subdepartmentId", parseInt(e.target.value))}
                                                >
                                                    <option value=""> </option>
                                                    {stocks.model.departmentId && <>
                                                        {subDepartments.table.map((subdepartment: any, index: any) => {
                                                            return <option key={index} value={subdepartment.id}>{subdepartment.name}</option>
                                                        })}
                                                    </>}
                                                </Form.Select>
                                            </Form.Group> */}
                                        </Col>
                                        <Col md={12} className="mt-2">
                                            <Controller
                                                control={control}
                                                render={({ field: { onChange, onBlur, value, ref }, fieldState, formState, }) => {
                                                    return <ZSingleSelectDropdown
                                                        id="manufacturer"
                                                        canClear
                                                        label={<ZLabel>Manufacturer</ZLabel>}
                                                        placeholder="Select a manufacturer"
                                                        options={manufacturers.table}
                                                        value={manufacturers.table.find(s => s.id === value)}
                                                        validation={
                                                            {
                                                                invalid: errors.manufacturerId != null,
                                                                error: errors.manufacturerId?.message
                                                            }
                                                        }
                                                        onBlur={onBlur}
                                                        optionKey={(opt) => opt.id}
                                                        disabled={() => false}
                                                        display={(opt) => opt.name ?? "Unknown"}
                                                        onSelectionChange={(selectedManufacturer) => {
                                                            handleModelChange("manufacturerId", selectedManufacturer?.id ?? null);
                                                            selectedManufacturer ? onChange(selectedManufacturer.id) : onChange(null);
                                                        }}
                                                        hasSearch
                                                    />
                                                }}
                                                name={'manufacturerId'}
                                            />
                                        </Col>
                                        <Col md={12} className="mt-2">
                                            <Controller
                                                control={control}
                                                rules={{}}
                                                render={({ field: { onChange, onBlur, value, ref }, fieldState, formState, }) => {
                                                    return <ZMultiSelectDropdown
                                                        id="stockGroups"
                                                        canClear
                                                        onAddNewOption={async (x) => {
                                                            dispatch(createStockGroup({
                                                                name: x,
                                                                id: -1,
                                                                modelId: "",
                                                                locked: false,
                                                            })).then(r => {
                                                                dispatch(getStockGroups());
                                                                const newStockGroupIds = [...value, r.data[0]];
                                                                handleModelChange("stockGroupIds", newStockGroupIds ?? []);
                                                                newStockGroupIds ? onChange(newStockGroupIds) : onChange([]);
                                                            });
                                                        }}
                                                        maxShownSelectedOptions={2}
                                                        placeholder="Select stock groups"
                                                        label={<ZLabel>Stock Groups</ZLabel>}
                                                        options={stocks.stockGroupsDropdown}
                                                        values={stocks.stockGroupsDropdown.filter(sg => value.some(v => v === sg.id))}
                                                        validation={
                                                            {
                                                                invalid: errors.stockGroupIds != null,
                                                                error: errors.stockGroupIds?.message
                                                            }
                                                        }
                                                        value={(opt) => opt.id}
                                                        disabled={() => false}
                                                        display={(opt) => opt.label ?? "Unknown"}
                                                        onSelectionChange={(stockGroupIds) => {
                                                            // handleModelChange("stockGroupIds", stockGroupIds.map(sg => sg.id));
                                                            // onChange(stockGroupIds);
                                                            handleModelChange("stockGroupIds", stockGroupIds.map(sg => sg.id) ?? []);
                                                            stockGroupIds ? onChange(stockGroupIds.map(sg => sg.id)) : onChange([]);
                                                        }}
                                                        hasSearch
                                                    />
                                                }}
                                                name={'stockGroupIds'}
                                            />
                                            {/* <Form.Group>
                                                <Form.Label className="form-check-label">Stock Groups</Form.Label>
                                                <CustomDropDown items={stocks.stockGroupsDropdown}
                                                    handleChecked={handleCheckedDropdown}
                                                    className="custom-checkbox-drop-down"
                                                    displayDropdown={showMenu}
                                                    setDisplayDropdown={setShowMenu}
                                                />
                                            </Form.Group> */}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={12} className="mt-2">
                                            <Col md={12} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "nowrap" }}>
                                                <ZLabel preventSelect>Schedule</ZLabel>
                                                {stocks.model.isPbs && <img src={PBS} alt="PBS" className="mb-1 flex-row-reverse" style={{ maxWidth: "35px" }} />}
                                            </Col>
                                            <Controller
                                                control={control}
                                                rules={{}}
                                                render={({ field: { onChange, onBlur, value } }) =>
                                                    <ZTextInput
                                                        disabled
                                                        value={value ?? ""}
                                                        key="text"
                                                        validation={
                                                            {
                                                                invalid: errors.schedule != null,
                                                                error: errors.schedule?.message
                                                            }
                                                        }
                                                        onChange={(v) => {
                                                            handleModelChange("schedule", v);
                                                            onChange(v);
                                                        }}
                                                        onBlur={onBlur}
                                                    />
                                                }
                                                name={'schedule'}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6} className="d-flex flex-column justify-content-end mt-2">
                                            <Controller
                                                control={control}
                                                rules={{
                                                    min: { value: 0, message: "Minimum Order Quantity cannot be negative." },
                                                    max: { value: 9999, message: "Minimum order quantity cannot exceed 9999." },
                                                    required: { value: true, message: "Please enter a minimum order quantity." }
                                                }}
                                                render={({ field: { onChange, onBlur, value } }) =>
                                                    <ZNumberInput
                                                        type="qty"
                                                        label={RequiredComponent(<OverflowLabel text="Minimum Order Quantity" isVisible />, "This field is required")}
                                                        placeholder="Enter a minimum order quantity"
                                                        value={value}
                                                        validation={
                                                            {
                                                                invalid: errors.minOrderQty != null,
                                                                error: errors.minOrderQty?.message
                                                            }
                                                        }
                                                        onChange={(v) => {
                                                            handleModelChange("minOrderQty", v ?? undefined);
                                                            onChange(v);
                                                        }}
                                                        onBlur={() => {
                                                            onBlur();
                                                        }}
                                                    />
                                                }
                                                name={'minOrderQty'}
                                            />
                                            {/* <FormInput
                                                max={999}
                                                min={0}
                                                label="Minimum Order Quantity"
                                                type="number"
                                                required
                                                name="stock-name"
                                                value={stocks.model.minOrderQty}
                                                onChange={(e) => handleModelChange("minOrderQty", parseInt(e.target.value))}
                                            /> */}
                                        </Col>
                                        <Col md={6} className="d-flex flex-column justify-content-end mt-2">
                                            <Controller
                                                control={control}
                                                rules={{
                                                    min: { value: 1, message: "Cannot have a carton size of 0." },
                                                    max: { value: 9999, message: "Maximum stock level cannot exceed 9999." },
                                                    required: { value: true, message: "Please enter a carton size." }
                                                }}
                                                render={({ field: { onChange, onBlur, value } }) =>
                                                    <ZNumberInput
                                                        type="qty"
                                                        label={RequiredComponent(<OverflowLabel text="Carton Size" isVisible />, "This field is required")}
                                                        placeholder="Enter a carton size"
                                                        value={value}
                                                        validation={
                                                            {
                                                                invalid: errors.cartonSize != null,
                                                                error: errors.cartonSize?.message
                                                            }
                                                        }
                                                        onChange={(v) => {
                                                            handleModelChange("cartonSize", v ?? null);
                                                            onChange(v);
                                                        }}
                                                        onBlur={onBlur}
                                                    />
                                                }
                                                name={'cartonSize'}
                                            />
                                            {/* <FormInput
                                                max={999}
                                                min={1}
                                                label="Carton Size"
                                                type="number"
                                                required
                                                containerClass="align-bottom"
                                                name="carton-size"
                                                value={stocks.model.cartonSize}
                                                onChange={(e) => handleModelChange("cartonSize", parseInt(e.target.value))}
                                            /> */}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6} className="d-flex flex-column justify-content-end mt-2">
                                            <Controller
                                                control={control}
                                                rules={{
                                                    min: { value: 0, message: "Minimum stock level cannot be negative." },
                                                    max: { value: 9999, message: "Minimum stock level cannot exceed 9999." },
                                                    required: { value: true, message: "Please enter a minimum stock level." }
                                                }}
                                                render={({ field: { onChange, onBlur, value } }) =>
                                                    <ZNumberInput
                                                        type="qty"
                                                        label={RequiredComponent(<OverflowLabel text="Minimum Stock Level" isVisible />, "This field is required")}
                                                        placeholder="Enter a minimum stock level"
                                                        value={value}
                                                        validation={
                                                            {
                                                                invalid: errors.minStockLevel != null,
                                                                error: errors.minStockLevel?.message
                                                            }
                                                        }
                                                        onChange={(v) => {
                                                            handleModelChange("minStockLevel", v ?? null);
                                                            onChange(v);
                                                        }}
                                                        onBlur={onBlur}
                                                    />
                                                }
                                                name={'minStockLevel'}
                                            />
                                            {/* <FormInput
                                                max={999}
                                                label="Minimum Stock Level"
                                                min={0}
                                                type="number"
                                                required
                                                name="min-stock-Level"
                                                value={stocks.model.minStockLevel}
                                                onChange={(e) => handleModelChange("minStockLevel", parseInt(e.target.value))}
                                            /> */}
                                        </Col>
                                        <Col md={6} className="d-flex flex-column justify-content-end mt-2">
                                            <Controller
                                                control={control}
                                                rules={{
                                                    min: { value: 0, message: "Maximum stock level can't be negative." },
                                                    max: { value: 999999999, message: "Maximum stock level can't exceed 999,999,999." },
                                                }}
                                                render={({ field: { onChange, onBlur, value } }) =>
                                                    <ZNumberInput
                                                        type="qty"
                                                        label={<ZLabel><OverflowLabel text="Maximum Stock Level" isVisible /></ZLabel>}
                                                        placeholder="Enter a maximum stock level"
                                                        value={value}
                                                        validation={
                                                            {
                                                                invalid: errors.maxStockLevel != null,
                                                                error: errors.maxStockLevel?.message
                                                            }
                                                        }
                                                        onChange={(v) => {
                                                            handleModelChange("maxStockLevel", v ?? null);
                                                            onChange(v);
                                                        }}
                                                        onBlur={onBlur}
                                                    />
                                                }
                                                name={'maxStockLevel'}
                                            />
                                            {/* <FormInput
                                                max={999}
                                                min={0}
                                                label="Maximum Stock Level"
                                                type="number"
                                                onKeyDown={(e) => checkIfNumber(e)}
                                                name="max-stock-Level"
                                                value={stocks.model.maxStockLevel ?? undefined}
                                                onChange={(e) => handleModelChange("maxStockLevel", e.target.value ? parseInt(e.target.value) : null)}
                                            /> */}
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={4} className="Stock Details mt-2">
                                    <h6 className="form-title py-1">Pricing Details</h6>
                                    <Row>
                                        <Col md={6} className="d-flex flex-column justify-content-end">
                                            <Controller
                                                control={control}
                                                rules={{
                                                    required: { value: true, message: "Please enter a cost price." },
                                                    min: { value: 0, message: "Cost excl GST cannot be negative." },
                                                    max: { value: 9999999.99, message: "Cost excl GST cannot exceed $9,999,999.99." }
                                                }}
                                                render={({ field: { onChange, onBlur, value } }) =>
                                                    <ZNumberInput
                                                        type="price"
                                                        label={
                                                            <ZLabel>
                                                                <OverflowLabel text={"Cost Excl GST"} isVisible />
                                                                <ZTooltip
                                                                    enabled
                                                                    text="Freedom reference only, will not be sent down"
                                                                >
                                                                    <ZIcon
                                                                        iconClassName="ms-1"
                                                                        icon={ZIconType.Information}
                                                                        size="14px"
                                                                    />
                                                                </ZTooltip>
                                                            </ZLabel>
                                                        }
                                                        placeholder="Enter a cost excl gst"
                                                        value={value}
                                                        validation={
                                                            {
                                                                invalid: errors.costEx != null,
                                                                error: errors.costEx?.message
                                                            }
                                                        }
                                                        onChange={(x) => { onChange(x); invalidateStock(); }}
                                                        onBlur={onBlur}
                                                    />
                                                }
                                                name={'costEx'}
                                            />
                                        </Col>
                                        <Col md={6} className="d-flex flex-column justify-content-end">
                                            <Controller
                                                control={control}
                                                rules={{
                                                    required: { value: true, message: "Please select a GST type." }
                                                }}
                                                render={({ field: { onChange, onBlur, value, ref }, fieldState, formState, }) => {
                                                    return <ZSingleSelectDropdown
                                                        label={<ZLabel>GST</ZLabel>}
                                                        options={Object.values(GSTCode)}
                                                        placeholder="Choose a GTS option"
                                                        value={value}
                                                        validation={
                                                            {
                                                                invalid: errors.gstCode != null,
                                                                error: errors.gstCode?.message
                                                            }
                                                        }
                                                        onBlur={onBlur}
                                                        optionKey={(opt) => opt}
                                                        disabled={() => false}
                                                        display={(opt) => opt + " - " + gstCodes.get(GSTCode[opt as keyof typeof GSTCode]) ?? "Unknown"}
                                                        onSelectionChange={(code) => {
                                                            handleModelChange("gstCode", code);
                                                            code && onChange(code);
                                                        }}
                                                    />
                                                }}
                                                name={'gstCode'}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="mt-2">
                                        <Col md={6}>
                                            <ZNumberInput
                                                type="percent"
                                                value={stocks.markup}
                                                label={<ZLabel>Markup</ZLabel>}
                                                disabled
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <ZNumberInput
                                                type="percent"
                                                value={stocks.gp}
                                                label={<ZLabel>GP</ZLabel>}
                                                disabled
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6} className="d-flex flex-column justify-content-end mt-2">
                                            <Controller
                                                control={control}
                                                rules={{
                                                    required: { value: true, message: "Please enter a base sell price." },
                                                    min: { value: 0, message: "Base sell price can't be negative." },
                                                    max: { value: 9999999.99, message: "Base sell price cannot exceed $9,999,999.99." }
                                                }}
                                                render={({ field: { onChange, onBlur, value } }) =>
                                                    <ZNumberInput
                                                        type="price"
                                                        label={
                                                            <ZLabel>
                                                                <OverflowLabel text={"Base Sell Price"} isVisible />
                                                            </ZLabel>
                                                        }
                                                        placeholder="Enter a base sell price"
                                                        value={value}
                                                        validation={
                                                            {
                                                                invalid: errors.baseSellPrice != null,
                                                                error: errors.baseSellPrice?.message
                                                            }
                                                        }
                                                        onChange={(x) => { onChange(x); invalidateStock(); }}
                                                        onBlur={onBlur}
                                                    />
                                                }
                                                name={'baseSellPrice'}
                                            />
                                        </Col>
                                        <Col md={6} className="d-flex flex-column justify-content-end mt-2">
                                            <Controller
                                                control={control}
                                                rules={{
                                                    min: { value: 0, message: "RRP can't be negative." },
                                                    max: { value: 9999999.99, message: "RRP cannot exceed $9,999,999.99." }
                                                }}
                                                render={({ field: { onChange, onBlur, value } }) =>
                                                    <ZNumberInput
                                                        type="price"
                                                        label={<ZLabel>RRP</ZLabel>}
                                                        placeholder="Enter an RRP"
                                                        value={value ?? undefined}
                                                        validation={
                                                            {
                                                                invalid: errors.rrp != null,
                                                                error: errors.rrp?.message
                                                            }
                                                        }
                                                        onChange={(x) => { onChange(x); invalidateStock(); }}
                                                        onBlur={onBlur}
                                                    />
                                                }
                                                name={'rrp'}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6} className="d-flex flex-column justify-content-end mt-2">
                                            <Form.Group>
                                                <ZLabel>Stock Flags</ZLabel>
                                                <Controller
                                                    control={control}
                                                    render={({ field: { onChange, onBlur, value } }) =>
                                                        <Form.Check
                                                            type="switch"
                                                            id="stock-active"
                                                            label="Active"
                                                            className="custom-control-checkbox mb-2 checkboxborder"
                                                            checked={value}
                                                            onChange={(x) => { onChange(x); invalidateStock(); }}
                                                            onBlur={onBlur}
                                                            name={"active"}
                                                        />
                                                    }
                                                    name={'active'}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6} className="d-flex flex-column justify-content-end mt-2">
                                            <Form.Group>
                                                <Controller
                                                    control={control}
                                                    render={({ field: { onChange, onBlur, value } }) =>
                                                        <Form.Check
                                                            type="switch"
                                                            id="stock-ethical"
                                                            label="Ethical"
                                                            className="custom-control-checkbox mb-2 checkboxborder"
                                                            checked={value ?? undefined}
                                                            onChange={(x) => { onChange(x); invalidateStock(); }}
                                                            onBlur={onBlur}
                                                            name={"ethical"}
                                                        />
                                                    }
                                                    name={'ethical'}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <ZFeatureFlag flag="ZFreedom.Stores.LockDownFields" value={true}>
                                            <Col md={6} className="d-flex flex-column justify-content-end mt-2">
                                                <Form.Group>
                                                    <Controller
                                                        control={control}
                                                        render={({ field: { onChange, onBlur, value } }) =>
                                                            <Form.Check
                                                                type="switch"
                                                                id="stock-locked"
                                                                label="Locked"
                                                                className="custom-control-checkbox mb-2 checkboxborder"
                                                                checked={value}
                                                                onChange={(x) => { onChange(x); invalidateStock(); }}
                                                                onBlur={onBlur}
                                                                name={"locked"}
                                                            />
                                                        }
                                                        name={'locked'}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </ZFeatureFlag>
                                    </Row>
                                    <Row>
                                        <Col md={12}>
                                            <Controller
                                                control={control}
                                                render={({ field: { onChange, onBlur, value } }) =>
                                                    <FormInput
                                                        label="Additional Order Information"
                                                        type="textarea"
                                                        spellCheck={false}
                                                        className="textarea-noresize"
                                                        rows="10"
                                                        name="stock-additional-order-information"
                                                        value={value ?? undefined}
                                                        onChange={(x) => { onChange(x); invalidateStock(); }}
                                                        onBlur={onBlur}
                                                        style={{ minHeight: "117px" }}
                                                    />
                                                }
                                                name={'additionalOrderInformation'}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={4} className="d-flex flex-column pt-2">
                                    {/* <Row className="flex-column flex-grow-1" style={{ minHeight: "300px", marginRight: "-1.65rem" }}> */}
                                        <Row className="mb-2">
                                        <Col xs={6} className="d-flex align-items-center"><h6 className="my-0 form-title py-1">PDE Details</h6></Col>
                                            <Col xs={6} className="d-flex justify-content-end gap-2">
                                                <ZButton buttonSize="sm" icon={ZIconType.Add} variant="light" onClick={() => {
                                                    trigger();
                                                    setSelectedPde(pde => {
                                                        pdeGridRef.current?.clearRowSelection();
                                                        handleChange("showPdePopup", true);
                                                        return undefined;
                                                    })
                                                }} />
                                                <ZTooltip
                                                    enabled={selectedPde == null}
                                                    placement="top"
                                                    text="Please select a PDE to edit"
                                                >
                                                    <ZButton buttonSize="sm" icon={ZIconType.Edit} variant="light" disabled={selectedPde == null} onClick={() => {
                                                        trigger();
                                                        setSelectedPde(pde => {
                                                            return pdes.find(p => p.rowId === pde!.rowId);
                                                        })
                                                        handleChange("showPdePopup", true);
                                                    }} />
                                                </ZTooltip>
                                                <ZTooltip
                                                    enabled={selectedPde == null || selectedPde.isSystem}
                                                    placement="top"
                                                    text={
                                                        selectedPde == null ? `Please select a PDE to delete`
                                                            : selectedPde.isSystem ? `System PDEs cannot be deleted` : ``
                                                    }
                                                >
                                                    <ZButton buttonSize="sm" icon={ZIconType.Delete} variant="light" disabled={selectedPde == null || selectedPde.isSystem} onClick={() => {
                                                        if (selectedPde && !selectedPde.isSystem) {
                                                            setShowDeletePdeConfirmPrompt(true);
                                                        } else {
                                                            dispatch(showToast({ value: [`You cannot delete a system PDE!`] }))
                                                        }
                                                    }} />
                                                </ZTooltip>
                                            </Col>
                                        </Row>
                                        <AgGrid
                                            ref={pdeGridRef}
                                            containerStyle={{minHeight: "300px"}}
                                            rowData={pdes}
                                            getRowId={(p: GetRowIdParams<PdeRowData, any>) => p.data.rowId}
                                            columnDefs={pdeColumnDefs}
                                            loading={false}
                                            rowSelectionType="single"
                                            onSelectionChanged={(rows) => setSelectedPde(rows.at(0) as PdeRowData | undefined)}
                                        />
                                        <PDE 
                                            selectedPde={selectedPde} 
                                            pdes={pdes} 
                                            stockModel={getValues()} 
                                            formErrors={errors} 
                                            showPopup={stocks.showPdePopup} 
                                            onHide={closePdePopup} 
                                            onSubmit={handleNewPde}
                                        />
                                    {/* </Row> */}
                                        <Row className="mt-3 mb-2">
                                            <Col xs={6} className="d-flex align-items-center"><h6 className="my-0 form-title">APN Details</h6></Col>
                                                <Col xs={6} className="d-flex justify-content-end gap-2">
                                                    <ZButton buttonSize="sm" icon={ZIconType.Add} variant="light" onClick={() => {
                                                        trigger();
                                                        setSelectedApn(apn => {
                                                            apnGridRef.current?.clearRowSelection();
                                                            handleChange("showApnPopup", true);
                                                            return undefined;
                                                        })
                                                    }} />
                                                    <ZTooltip
                                                        enabled={selectedApn == null || selectedApn.isSystem}
                                                        placement="top"
                                                        text={
                                                            selectedApn == null ? `Please select an APN to edit`
                                                                : selectedApn.isSystem ? `System APNs cannot be edited` : ``
                                                        }
                                                    >
                                                        <ZButton buttonSize="sm" icon={ZIconType.Edit} variant="light" disabled={selectedApn == null || selectedApn.isSystem} onClick={() => {
                                                            trigger();
                                                            setSelectedApn(apn => {
                                                                return apns.find(a => a.rowId === apn!.rowId);
                                                            })
                                                            handleChange("showApnPopup", true);
                                                        }} />
                                                    </ZTooltip>
                                                    <ZTooltip
                                                        enabled={selectedApn == null || selectedApn.isSystem}
                                                        placement="top"
                                                        text={
                                                            selectedApn == null ? `Please select an APN to delete`
                                                                : selectedApn.isSystem ? `System APNs cannot be deleted` : ``
                                                        }
                                                    >
                                                        <ZButton buttonSize="sm" icon={ZIconType.Delete} variant="light" disabled={selectedApn == null || selectedApn.isSystem} onClick={() => {
                                                            if (selectedApn && !selectedApn?.isSystem) {
                                                                setShowDeleteApnConfirmPrompt(true);
                                                            }
                                                        }} />
                                                    </ZTooltip>
                                                </Col>
                                        </Row>
                                        <AgGrid
                                            ref={apnGridRef}
                                            containerStyle={{minHeight: "300px"}}
                                            rowData={apns}
                                            getRowId={(p: GetRowIdParams<ApnRowData, any>) => p.data.rowId}
                                            columnDefs={apnColumnDefs}
                                            loading={false}
                                            rowSelectionType="single"
                                            onSelectionChanged={(rows) => setSelectedApn(rows.at(0) as ApnRowData | undefined)}
                                        />
                                        <APN 
                                            selectedApn={selectedApn} 
                                            apns={apns} 
                                            stockModel={getValues()} 
                                            formErrors={errors} 
                                            showPopup={stocks.showApnPopup} 
                                            onHide={closeApnPopup} 
                                            onSubmit={handleNewApn}
                                        />
                                    {/* </Row> */}
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col sm={6}>
                                    <Link to="/app/managed" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
                                        <i className="mdi mdi-arrow-left"></i> Back to All Stocks{" "}
                                    </Link>
                                </Col>
                                <Col sm={6} className="d-flex flex-row-reverse">
                                    <Button variant="primary" className="btn m-1 me-0 cta-button" disabled={stocks.loading || stocks.loadingSync} onClick={() => {
                                        handleChange("loading", true);
                                        handleFormSubmit();
                                    }}>
                                        {stocks.loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
                                        {stocks.loading ? <>Saving...</> : <>Save</>}
                                    </Button>
                                    <Button variant="light" className="btn m-1 cta-button" disabled={stocks.loadingSync || stocks.loading} onClick={() => setOpenSyncWindow(true)}>
                                        {stocks.loadingSync ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
                                        {stocks.loadingSync ? <>Saving...</> : <>Save & Sync</>}
                                    </Button>
                                    {stocks.saveSuccess ?
                                        <TooltipIcon text="Stock saved successfully" placement="top" icon="mdi-check-circle-outline" size={24} colour={getMainColors().success} classes={["m-1", "mdi"]} />
                                        : <></>
                                    }
                                </Col>
                            </Row>
                        </Form>
                    </Row>
                </Col>
            </Row>
        </>
    );
};